import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { ROUTE_FREE_TV, ROUTE_PARTNER_FREE_TV } from '@/constants/routes';

// UTILITY
import { analyticsPageLoaded } from '@/utility/analytics';
import { getS3ImageUrl } from '@/utility/content';
import { getLink } from '@/utility/routes';

// HOOK
import useActions from '@/hooks/useActions';

// STORE
import { PagesActions } from '@/store/actions';

// COMPONENTS
import FAQ from '@/components/FAQ';
import Loader from '@/components/Loader';

// HELP
import Hero from './help/Hero';
import Services from './help/Services';
import Steps from './help/Steps';
import Channels from './help/Channels';
import Info from './help/Info';

// STYLES
import { ContentWrap } from './styles';

const FreeTVLanding = () => {
  const [t] = useTranslation();

  const [getPagesContent] = useActions([
    () => PagesActions.getPagesContent.action({ pageSlug: 'free-tv' }),
  ]);

  const { partner, partnerLoading, contentLoading } = useSelector(
    ({ partner: partnerStore, pages }) => ({
      partner: partnerStore.data,
      partnerLoading: partnerStore.loadings.partner,
      contentLoading: pages.loading,
    }),
  );

  useEffect(() => {
    getPagesContent();
  }, []);

  const isPartner = !!partner.slug;

  const faqData = [
    {
      title: t('freeTv.faq.item1.question'),
      text: t('freeTv.faq.item1.answer'),
    },
    {
      title: t('freeTv.faq.item2.question'),
      text: t('freeTv.faq.item2.answer'),
    },
    {
      title: t('freeTv.faq.item3.question'),
      text: t('freeTv.faq.item3.answer'),
    },
    {
      title: t('freeTv.faq.item4.question'),
      text: t('freeTv.faq.item4.answer'),
    },
  ];

  const seoData = {
    canonicalUrl: `${siteUrl}${ROUTE_FREE_TV}`,
    ogUrl: isPartner
      ? `${siteUrl}${getLink(ROUTE_PARTNER_FREE_TV, { partner: partner.slug })}`
      : `${siteUrl}${ROUTE_FREE_TV}`,
    title: 'Streaming Free Live TV',
    description:
      'Stream Free Live TV Anytime on Your TV with MyBundle Free TV.  MyBundle gives you FREE access to live TV. Simply choose a device you want to watch on and start streaming the way you desire. ',
    image: getS3ImageUrl('/free-tv/hero.png'),
  };

  return (
    <ContentWrap className="sc">
      {!partnerLoading && (
        <Helmet onChangeClientState={analyticsPageLoaded} titleTemplate="%s | MyBundle">
          <title>{seoData.title}</title>
          <meta property="og:title" content={seoData.title} />
          <meta property="og:site_name" content="MyBundle" />
          <link rel="canonical" href={seoData.canonicalUrl} />
          <meta property="og:url" content={seoData.ogUrl} />
          <meta property="og:description" content={seoData.description} />
          <meta name="description" content={seoData.description} />
          <meta property="og:image" content={seoData.image} />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="twitter:title" content={seoData.title} />
          <meta name="twitter:description" content={seoData.description} />
          <meta name="twitter:image" content={seoData.image} />
          <meta name="twitter:site" content="@mybundletv" />
          <meta name="twitter:creator" content="@mybundletv" />
          <meta name="twitter:card" content="summary" />
        </Helmet>
      )}
      <Hero />
      <Steps />
      <Services />
      <Channels />
      <Info />
      <FAQ className="section--grey" data={faqData} />
      <Loader active={partnerLoading || contentLoading} fullFilled />
    </ContentWrap>
  );
};

export default React.memo(FreeTVLanding);
